import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { IconSize } from '@databox/icons/dist/types';

import type { Service } from 'webapp-types';
import {
	BillingFeature,
	DataPerformanceSummaryType,
	Direction,
	PopupEvent,
} from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isAlive } from 'webapp/utils/common';

import trendIconName from 'webapp/helpers/trend-icon-name';
import didUpdate from 'webapp/modifiers/did-update';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DataPerformancePopupSummaryComponent from 'webapp/components/data-performance/popup-summary';
import DataPerformanceSummaryBaseComponent from 'webapp/components/data-performance/summary-base';
import ToolTipComponent from 'webapp/components/tool-tip';

import type { DataPerformanceSummaryObject } from 'webapp/services/model-manager/data-performance-summaries';

interface Args {
	object: DataPerformanceSummaryObject | null;
	isDataIncomplete?: boolean;
	isDataEmpty?: boolean;
	trackingOrigin?: string; // Passed for Boards: 'designer' | 'preview_databoard' | 'databoards' | 'datablock_explorer'
	onPopupOpenStateChange?: (isOpen: boolean) => void;
	iconSize?: IconSize;
}

export default class DataPerformanceSummary extends DataPerformanceSummaryBaseComponent<Args> {
	@service('account') declare accountService: Service.Account;
	@service('ai-chat') declare aiChatService: Service.AIChat;
	@service('popup') declare popupService: Service.Popup;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('billing') declare billingService: Service.Billing;

	@tracked isPopupOpen = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.resetTrend();
	}

	get object() {
		return this.args.object;
	}

	get isDataIncomplete() {
		const isModelDataIncomplete = this.model?.isDataIncomplete;
		return isModelDataIncomplete === undefined
			? !!this.args.isDataIncomplete
			: isModelDataIncomplete;
	}

	get isDataEmpty() {
		const isModelDataEmpty = this.model?.isDataEmpty;
		return isModelDataEmpty === undefined ? !!this.args.isDataEmpty : isModelDataEmpty;
	}

	get preventOpenIfDataIncompleteOrEmpty() {
		return this.model?.generativeAIEnabled && this.isDataIncompleteOrEmpty;
	}

	get noDataClass() {
		return this.isDataIncomplete ? 'unavailable' : 'disabled';
	}

	get trendClass() {
		return this.isDataIncompleteOrEmpty ? this.noDataClass : this.model?.trend;
	}

	get buttonClass() {
		return this.model?.generativeAIEnabled ? this.trendClass : 'disabled';
	}

	get showButton() {
		return this.mmDataPerformanceSummariesService.showInUI;
	}

	get iconSize(): IconSize {
		return this.args.iconSize ?? 'dynamic';
	}

	@action
	openSummary(e: PointerEvent): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		if (this.preventOpenIfDataIncompleteOrEmpty) {
			return;
		}

		if (!this.billingService.checkIfCan(BillingFeature.GENERATIVE_AI, true)) {
			return;
		}

		if (this.model && this.aiChatService.openGenieForPerformanceSummary(this.model)) {
			return;
		}

		if (this.popupService.isOpen(DataPerformancePopupSummaryComponent)) {
			return;
		}

		const target = e.target as HTMLElement | null,
			attachTo: HTMLElement | null =
				target?.closest('.data-performance-summary-trigger') || null;

		if (!attachTo) {
			return;
		}

		const popup = this.popupService.createContext(
				DataPerformancePopupSummaryComponent,
				attachTo,
				{
					popupClass: 'popup-type-data-performance-summary-context',
					directions: [Direction.RIGHT, Direction.LEFT, Direction.DOWN, Direction.UP],
					allowStacked: true,
				},
			),
			model = this.model;

		if (popup && model) {
			popup.open({
				model,
			});

			this.isPopupOpen = true;
			if (this.args.onPopupOpenStateChange) {
				this.args.onPopupOpenStateChange(true);
			}

			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this.isPopupOpen = false;
					if (this.args.onPopupOpenStateChange) {
						this.args.onPopupOpenStateChange(false);
					}
				}
			});

			this.trackPopupOpen();
		}
	}

	private trackPopupOpen() {
		let trackingOrigin = '';

		switch (this.object?.objectType) {
			case DataPerformanceSummaryType.GOAL:
				trackingOrigin = 'goals';
				break;
			case DataPerformanceSummaryType.BOARD:
			case DataPerformanceSummaryType.BOARD_PREVIEW:
				trackingOrigin = this.args.trackingOrigin || '';
				break;
			case DataPerformanceSummaryType.BLOCK: {
				const isInDesigner =
					this.navigationService.activatedRoute.startsWith('designer.board');
				trackingOrigin = isInDesigner ? 'designer_datablock' : 'preview_datablock';
				break;
			}
			case DataPerformanceSummaryType.DYNAMIC:
				trackingOrigin = 'metrics_detail';
				break;
			default:
				break;
		}

		const model = this.model;
		if (model) {
			this.mmDataPerformanceSummariesService.trackEvent(
				TrackingEvent.DATA_PERFORMANCE_SUMMARY_POPUP_OPENED,
				model,
				{
					origin: trackingOrigin,
				},
			);
		}
	}
	<template>
		{{#if this.showButton}}
			<div
				role="button"
				class="data-performance-summary-trigger trend--{{this.buttonClass}}
					{{if this.preventOpenIfDataIncompleteOrEmpty 'no-data'}}
					{{if this.isPopupOpen 'active'}}"
				data-sample-v={{this.model.sampleHash}}
				{{on "click" this.openSummary}}
				{{didUpdate this.resetTrend this.isDataIncompleteOrEmpty}}
			>
				{{#if this.preventOpenIfDataIncompleteOrEmpty}}
					<ToolTipComponent
						@text="Currently unavailable due to incomplete data."
						@maxWidth={{200}}
					/>
				{{/if}}
				<CommonUiIconComponent
					@name={{trendIconName this.buttonClass}}
					@size={{this.iconSize}}
					@class="data-performance-summary-trend"
				/>
			</div>
		{{/if}}
	</template>
}
